import { defineStore } from "pinia";
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { log } from "../common/utils/logging";
import { useUser } from "../composition-functions/user";
import { UserType } from "../composition-functions/user/UserType";
import { MenuItemType } from "../dashboard/enum.menuItemType";
import type { ActionMenuItem, DashboardSection, RouteMenuItem } from "../dashboard/model.dashboard";
import { useAdminDashboard } from "../dashboard/service.adminDashboard";
import { useMandantDashboard } from "../dashboard/service.mandantDashboard";
import { useMandant } from "../mandant/service.mandant";
import { assertNever } from "../util/assertNever";
import { QuickAccessContext } from "./enum.quickAccessContext";
import type { QuickAccessGroup, QuickAccessItem } from "./model.quickAccess";

type GenerateItemIdParams = {
  context: QuickAccessContext;
  title: string;
  subtitle: string;
};

function generateItemId({ context, title, subtitle }: GenerateItemIdParams): string {
  const titleSlug = title.replace(/\s/g, "-").toLowerCase();
  const subtitleSlug = subtitle.replace(/\s/g, "-").toLowerCase();

  return `${context}-${titleSlug}-${subtitleSlug}`;
}

type GetScoreForTextParams = {
  text: string;
  queryTerm: string;
  baseScore: number;
};

function getTextScore({ text, queryTerm, baseScore }: GetScoreForTextParams): number {
  if (text === queryTerm) return baseScore;
  if (text.startsWith(queryTerm)) return baseScore - 10;
  if (text.includes(queryTerm)) return baseScore - 20;
  return 0;
}

type BuildQuickAccessItemsParams = {
  dashboardSections: DashboardSection[];
  context: QuickAccessContext;
};

function buildQuickAccessItems({ dashboardSections, context }: BuildQuickAccessItemsParams): QuickAccessItem[] {
  const items: QuickAccessItem[] = [];

  for (const dashboardSection of dashboardSections) {
    if (!dashboardSection.isVisible) continue;

    for (const item of dashboardSection.items) {
      if (!item.isVisible || item.isDisabled) continue;

      const quickAccessItem: QuickAccessItem = {
        title: item.title,
        subtitle: item.subtitle,
        icon: item.icon,
        sectionName: context === QuickAccessContext.ADMIN ? `${dashboardSection.title} (Global)` : dashboardSection.title,
        context,
        navigationMetadata:
          item.type === MenuItemType.ROUTE
            ? { type: MenuItemType.ROUTE, to: item.to }
            : { type: MenuItemType.ACTION, onClick: item.onClick },
        id: generateItemId({ context, title: item.title, subtitle: item.subtitle }),
      };

      items.push(quickAccessItem);
    }
  }

  return items;
}

type FilterAndSortItemsParams = {
  items: QuickAccessItem[];
  query: string;
};

function filterAndSortItems({ items, query }: FilterAndSortItemsParams): QuickAccessItem[] {
  const queryTerms = query.split(/\s+/).filter(Boolean);

  const scoredItems = items.map((item) => ({
    item,
    score: getMatchScoreForItem({ item, queryTerms }),
  }));

  const matchingItems = scoredItems.filter(({ score }) => score > 0);
  const sortedByRelevance = matchingItems.sort((a, b) => b.score - a.score);

  return sortItemsByContext(sortedByRelevance.map(({ item }) => item));
}

type GetMatchScoreForItemParams = {
  item: QuickAccessItem;
  queryTerms: string[];
};

function getMatchScoreForItem({ item, queryTerms }: GetMatchScoreForItemParams): number {
  const lowercaseTitle = item.title.toLowerCase();
  const lowercaseSubtitle = item.subtitle.toLowerCase();
  let totalScore = 0;

  for (const queryTerm of queryTerms) {
    const titleScore = getTextScore({ text: lowercaseTitle, queryTerm, baseScore: 100 });
    const subtitleScore = getTextScore({ text: lowercaseSubtitle, queryTerm, baseScore: 70 });

    const bestScore = Math.max(titleScore, subtitleScore);

    if (bestScore === 0) {
      return 0;
    }

    totalScore += bestScore;
  }

  return totalScore;
}

function sortItemsByContext(items: QuickAccessItem[]): QuickAccessItem[] {
  const mandantItems = items.filter((item) => item.context !== QuickAccessContext.ADMIN);
  const adminItems = items.filter((item) => item.context === QuickAccessContext.ADMIN);
  return [...mandantItems, ...adminItems];
}

function groupItemsBySection(items: QuickAccessItem[]): Map<string, QuickAccessGroup> {
  const sortedItems = items.sort((firstItem, secondItem) => {
    const isFirstItemGlobal = firstItem.context === QuickAccessContext.ADMIN;
    const isSecondItemGlobal = secondItem.context === QuickAccessContext.ADMIN;

    if (isFirstItemGlobal && !isSecondItemGlobal) return 1;
    if (!isFirstItemGlobal && isSecondItemGlobal) return -1;

    return firstItem.sectionName.localeCompare(secondItem.sectionName);
  });

  const groups = new Map<string, QuickAccessGroup>();

  for (const item of sortedItems) {
    const key = `${item.context}-${item.sectionName}`;
    const existingGroup = groups.get(key);

    if (existingGroup) {
      existingGroup.quickAccessItems.push(item);
    } else {
      groups.set(key, {
        sectionName: item.sectionName,
        context: item.context,
        quickAccessItems: [item],
      });
    }
  }

  return groups;
}

export const useQuickAccess = defineStore("quickAccessStore", () => {
  const isOpen = ref(false);
  const searchQuery = ref("");
  const selectedIndex = ref(0);
  const isNavigatingWithKeyboard = ref(false);
  const searchInputRef = ref<null | HTMLInputElement>(null);

  const router = useRouter();
  const userStore = useUser();
  const mandantStore = useMandant();
  const adminDashboardStore = useAdminDashboard();
  const mandantDashboardStore = useMandantDashboard();

  function toggleQuickAccess(): void {
    isOpen.value ? closeQuickAccess() : openQuickAccess();
  }

  async function openQuickAccess(): Promise<void> {
    isOpen.value = true;
    searchQuery.value = "";
    selectedIndex.value = 0;
    await nextTick();
    focusSearchInput();
  }

  function focusSearchInput() {
    if (!searchInputRef.value) return;
    searchInputRef.value.focus();
  }

  function closeQuickAccess(): void {
    isOpen.value = false;
    searchQuery.value = "";
    selectedIndex.value = 0;
  }

  async function executeCurrentlySelected(): Promise<void> {
    const item = visibleItems.value[selectedIndex.value];
    if (item) {
      const { navigationMetadata } = item;
      try {
        switch (navigationMetadata.type) {
          case MenuItemType.ROUTE: {
            await handleRouteNavigation(navigationMetadata.to);
            break;
          }
          case MenuItemType.ACTION: {
            handleActionNavigation(navigationMetadata.onClick);
            break;
          }
          default: {
            assertNever(navigationMetadata);
          }
        }
      } catch (error) {
        log({ error });
      }

      closeQuickAccess();
    }
  }

  async function handleRouteNavigation(to: RouteMenuItem["to"]): Promise<void> {
    if (typeof to === "string") {
      window.location.href = to;
    } else {
      try {
        await router.push(to);
      } catch (error) {
        log({ error });
      }
    }
  }

  function handleActionNavigation(onClick: ActionMenuItem["onClick"]): void {
    onClick();
  }

  function buildAdminItems(): QuickAccessItem[] {
    return buildQuickAccessItems({
      dashboardSections: adminDashboardStore.adminDashboardConfiguration,
      context: QuickAccessContext.ADMIN,
    });
  }

  function buildMandantItems(): QuickAccessItem[] {
    return buildQuickAccessItems({
      dashboardSections: mandantDashboardStore.mandantDashboardConfiguration,
      context: QuickAccessContext.MANDANT,
    });
  }

  const allItems = computed((): QuickAccessItem[] => {
    const items: QuickAccessItem[] = [];

    try {
      if (userStore.userType && [UserType.ADMIN, UserType.SUPER_ADMIN].includes(userStore.userType)) {
        const adminItems = buildAdminItems();
        items.push(...adminItems);
      }

      if (mandantStore.currentMandant) {
        const mandantItems = buildMandantItems();
        items.push(...mandantItems);
      }
    } catch (error) {
      log({ error });
    }

    return items;
  });

  const normalizedSearchQuery = computed((): string => {
    return searchQuery.value.toLowerCase().trim();
  });

  const filteredSearchResults = computed((): QuickAccessItem[] => {
    return normalizedSearchQuery.value ? filterAndSortItems({ items: allItems.value, query: normalizedSearchQuery.value }) : [];
  });

  const groupedItems = computed((): Map<string, QuickAccessGroup> => {
    return normalizedSearchQuery.value ? new Map() : groupItemsBySection(allItems.value);
  });

  const visibleItems = computed((): QuickAccessItem[] => {
    return filteredSearchResults.value.length > 0 ? filteredSearchResults.value : allItems.value;
  });

  const shouldShowSearchSeparator = computed((): boolean => {
    return normalizedSearchQuery.value ? shouldShowContextSeparator(visibleItems.value) : false;
  });

  const shouldShowGroupSeparator = computed((): boolean => {
    return !normalizedSearchQuery.value ? shouldShowContextSeparator(visibleItems.value) : false;
  });

  function shouldShowContextSeparator(items: QuickAccessItem[]): boolean {
    const hasMandantItems = items.some((item) => item.context !== QuickAccessContext.ADMIN);
    const hasGlobalItems = items.some((item) => item.context === QuickAccessContext.ADMIN);
    return hasMandantItems && hasGlobalItems;
  }

  watch(filteredSearchResults, (newItems) => {
    if (newItems.length > 0 && normalizedSearchQuery.value) {
      selectedIndex.value = 0;
    }
  });

  return {
    isOpen,
    searchQuery,
    selectedIndex,
    isNavigatingWithKeyboard,
    filteredSearchResults,
    shouldShowSearchSeparator,
    groupedItems,
    shouldShowGroupSeparator,
    normalizedSearchQuery,
    searchInputRef,
    visibleItems,
    toggleQuickAccess,
    openQuickAccess,
    closeQuickAccess,
    executeCurrentlySelected,
  };
});
